import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Button,
  TextField,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import EmojiPicker from './EmojiPicker';
import {
  getCheckDuplicateSubcategory,
  getCheckDuplicateCategory,
  postCategoryForm,
  postSubcategoryForm,
} from '../../../../api/category';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

AddCategory.propTypes = {
  mainCategory: PropTypes.bool,
  categories: PropTypes.array,
  fetchData: PropTypes.func,
};

export default function AddCategory({ mainCategory, categories, fetchData }) {
  const [open, setOpen] = useState(false);
  const [icon, setIcon] = useState('fluent-emoji-flat:plus');

  const { enqueueSnackbar } = useSnackbar();

  const [category, setCategory] = useState(null);
  const [name, setName] = useState('');

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setCategory(null);
    setName('');
    setIcon('fluent-emoji-flat:plus');
  };
  const handleSave = async () => {
    if (!name) {
      enqueueSnackbar('Name is required', 'error');
      return;
    }

    try {
      if (!mainCategory) {
        const duplicate = await getCheckDuplicateSubcategory(-1, category.id, name);
        if (duplicate.data) {
          enqueueSnackbar('Subcategory already exists', { variant: 'error' });
          return;
        }
        const res = await postSubcategoryForm({ name, mainCategoryId: category.id });
        if (res.data) enqueueSnackbar(`Subcategory added`, { variant: 'success' });
        else enqueueSnackbar(`Subcategory add failed`, { variant: 'error' });
      } else {
        const duplicate = await getCheckDuplicateCategory(-1, name);
        if (duplicate.data) {
          enqueueSnackbar('Category already exists', { variant: 'error' });
          return;
        }
        const res = await postCategoryForm({ name, icon });
        if (res.data) enqueueSnackbar(`Category added`, { variant: 'success' });
        else enqueueSnackbar(`Category add failed`, { variant: 'error' });
      }
    } catch {
      enqueueSnackbar(`Something went wrong`, { variant: 'error' });
    }
    fetchData();
    handleClose();
  };
  return (
    <>
      <Button
        variant="contained"
        sx={{ mr: 1 }}
        size="small"
        color="secondary"
        onClick={handleClickOpen}
        startIcon={<Iconify icon="material-symbols:add" />}
      >
        Add New {mainCategory ? 'Category' : 'Subcategory'}
      </Button>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          Add new {mainCategory ? 'Category' : 'Subcategory'}
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          {!mainCategory ? (
            <>
              <DialogContentText sx={{ fontWeight: 'bold' }}>Select Category</DialogContentText>
              <Autocomplete
                sx={{
                  mt: 1,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': 'none',
                }}
                size="small"
                fullWidth
                type="new-password"
                options={categories}
                autoHighlight
                getOptionLabel={(option) => option?.name}
                value={category}
                onChange={(event, newValue) => {
                  setCategory(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                    }}
                    autoComplete="off"
                  />
                )}
              />{' '}
            </>
          ) : (
            <>
              <DialogContentText sx={{ fontWeight: 'bold' }}>Add Icon</DialogContentText>
              <EmojiPicker icon={icon} setIcon={setIcon} categories={categories}/>
            </>
          )}
          <>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Name</DialogContentText>{' '}
            <TextField
              value={name || ''}
              onChange={(e) => setName(e.target.value)}
              label=""
              size="small"
              variant="outlined"
              sx={{ width: '100%' }}
              autoComplete="off"
            />
          </>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150, mr: 2 }}
            onClick={handleSave}
            disabled={!name.trim()}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
